/**
 * @module geo/components
 *
 */
import { Observable } from "rxjs";
import { fromPromise } from "rxjs/observable/fromPromise";

import { registerExtensionCreatorP } from "../actions/creators/register.extension.creator";
import { REGISTER_EXTENSION } from "../payload/facade/REGISTER_EXTENSION";
import { Extension, ExtensionType } from "../types/extensions";
import {
  PanelWidgetExtension,
  PanelWidgetExtensionProperties,
  WidgetPanelType
} from "../types/extensions/panel.widget";
import {
  SearchResultExtension,
  SearchResultExtensionProperties
} from "../types/extensions/search.result";
import {
  SearchToolBarExtension,
  SearchToolBarExtensionProperties
} from "../types/extensions/search.toolbar";
import {
  ThematicsEntriesExtension,
  ThematicsEntriesExtensionProperties
} from "../types/extensions/thematic.entries";
import {
  ThematicsToolBarExtension,
  ThematicsToolBarExtensionProperties
} from "../types/extensions/thematics.toolbar";
import {
  TooltipExtension,
  TooltipExtensionProperties
} from "../types/extensions/tooltip";

import { Component } from "./component";

/**
 * General right panel of the application. (PRO layout)
 * @since v2.0.1
 */
export class RightPanel extends Component {
  defaultExtensionType = ExtensionType.WIDGET_PANEL;

  public buildExtension(properties: PanelWidgetExtensionProperties): Extension {
    const type = this.defaultExtensionType;
    properties.type = WidgetPanelType.RIGHTPANEL;
    return { type, properties } as Extension;
  }
}

/**
 * First handler of right panel, general application button. (PRO layout)
 * is part of [[RightPanel]]
 * @since v2.0.1
 */
export class ApplicationRightPanel extends Component {
  defaultExtensionType = ExtensionType.WIDGET_PANEL;

  public buildExtension(
    properties: PanelWidgetExtensionProperties
  ): PanelWidgetExtension {
    const type = this.defaultExtensionType;
    properties.type = WidgetPanelType.RIGHTPANEL;
    return { type, properties } as PanelWidgetExtension;
  }
}

/**
 * AnalysesRightPanel is part of [[RightPanel]]
 * @since 2.0.1
 */
export class AnalysesRightPanel extends Component {
  defaultExtensionType = ExtensionType.ANALYSES_TOOLBAR;
}

/**
 * StatsRightPanel is part of [[RightPanel]]
 * @since 2.0.1
 */
export class StatsRightPanel extends Component {
  defaultExtensionType = ExtensionType.STATS_TOOLBAR;
}

/**
 * The edition right panel.
 * EditionRightPanel is part of [[RightPanel]]
 * @since v2.0.1
 */
export class EditionRightPanel extends Component {
  // public setProperties() { };
}

/**
 * Handle in the pro canvas the Map right panel (for using thematic, etc).
 *
 * MapRightPanel is part of [[RightPanel]]
 * @since v2.0.1
 */
export class MapRightPanel extends Component {
  public buildExtension(
    properties:
      | ThematicsEntriesExtensionProperties
      | ThematicsToolBarExtensionProperties
  ): ThematicsEntriesExtension | ThematicsToolBarExtension {
    let type = ExtensionType.THEMATICS_TOOLBAR;
    if (properties.extensionType) {
      if (properties.extensionType === "toolbar") {
        type = ExtensionType.THEMATICS_TOOLBAR;
        return { type, properties } as ThematicsToolBarExtension;
      }
      if (properties.extensionType === "entry") {
        type = ExtensionType.THEMATICS;
        return { type, properties } as ThematicsEntriesExtension;
      }
    }
    return { type, properties } as ThematicsToolBarExtension;
  }
  // public setProperties() { };
}

/**
 * Handle the personnal pack interface in the right panel.
 *
 * PackPersoRightPanel is part of [[RightPanel]]
 *
 * @since v2.0.1
 */
export class PackPersoRightPanel extends Component {}

/**
 * a panel to handle global parameters.
 * GlobalParametersRightPanel is part of [[RightPanel]]
 * @since v2.0.1
 */
export class GlobalParametersRightPanel extends Component {}
/**
 * SearchResultsRightPanel is part of [[RightPanel]]
 * @since 2.0.1
 */
export class SearchResultsRightPanel extends Component {
  public defaultExtensionType = ExtensionType.MAP_TOOL;

  public buildExtension(
    properties:
      | SearchToolBarExtensionProperties
      | SearchResultExtensionProperties
      | TooltipExtensionProperties
  ): SearchToolBarExtension | SearchResultExtension | TooltipExtension {
    if (properties.extensionType) {
      if (properties.extensionType === "toolbar") {
        return this.buildToolBarExtension(
          properties as SearchToolBarExtensionProperties
        );
      }
      if (properties.extensionType === "result") {
        return this.buildResultExtension(
          properties as SearchResultExtensionProperties
        );
      }
      if (properties.extensionType === "tooltip") {
        return this.buildTooltipExtension(
          properties as TooltipExtensionProperties
        );
      }
    }
    return this.buildToolBarExtension(
      properties as SearchToolBarExtensionProperties
    );
  }

  public buildToolBarExtension(
    properties: SearchToolBarExtensionProperties
  ): SearchToolBarExtension {
    const type = ExtensionType.SEARCH_TOOLBAR;
    const props = { ...properties };
    if (properties.enableOptions && !properties.enable) {
      const opt = properties.enableOptions;
      props.enable = (searchContainer, selectedFeature) => {
        return (
          (!opt.searchId || opt.searchId === searchContainer.searches[0].id) &&
          (!opt.searchName ||
            opt.searchName === searchContainer.searches[0].name)
        );
      };
    }
    return { type, properties: props } as SearchToolBarExtension;
  }
  public buildResultExtension(
    properties: SearchResultExtensionProperties
  ): SearchResultExtension {
    const type = ExtensionType.SEARCHRESULT;
    const props = { ...properties };
    if (properties.enableOptions && !properties.enable) {
      const opt = properties.enableOptions;
      props.enable = (feature, searchContainer) => {
        console.log("eanble options :", opt);
        return (
          (!opt.searchId || opt.searchId === searchContainer.searches[0].id) &&
          (!opt.searchName ||
            opt.searchName === searchContainer.searches[0].name)
        );
      };
    }
    return { type, properties: props } as SearchResultExtension;
  }
  public buildTooltipExtension(
    properties: TooltipExtensionProperties
  ): TooltipExtension {
    const type = ExtensionType.TOOLTIP;
    const props = { ...properties };
    if (properties.enableOptions && !properties.enable) {
      const opt = properties.enableOptions;
      props.enable = (feature, searchContainer) => {
        return (
          (!opt.searchId || opt.searchId === searchContainer.searches[0].id) &&
          (!opt.searchName ||
            opt.searchName === searchContainer.searches[0].name)
        );
      };
    }
    return { type, properties: props } as TooltipExtension;
  }

  public addToolBarExtension(
    properties: SearchToolBarExtensionProperties
  ): Observable<any> {
    if (!this.store) {
      throw new Error("No store defined");
    }

    const props: SearchToolBarExtensionProperties = { key: "geoApi.toolbar" };
    Object.assign(props, properties);
    const extension = new REGISTER_EXTENSION(
      ExtensionType.SEARCH_TOOLBAR,
      props
    );
    console.log(extension);
    return fromPromise(
      registerExtensionCreatorP(extension, this.store.dispatch)
    );
  }
}
